import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss, Card, CardTitle, ResizablePane, PageHeader, Breadcrumb } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import ResizableSplit from 'components/common/ResizableSplit';
import { getToday } from 'utils/common_util';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import api from 'services/api';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import EmployeeMasterDetailTabs from './employeeMaster/EmployeeMasterDetailTabs';
import PhotoBulkUploadDialog from './employeeMaster/PhotoBulkUploadDialog';

const MASTER_GRID_ID = 'employee-master';
const MASTER_JOIN_KEYS = ['employeeId', 'empNo'];

const EMP_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '재직' },
  { value: 'ON_LEAVE', label: '휴직' },
  { value: 'TERMINATED', label: '퇴직' },
];

const EMPLOYMENT_CLASS_OPTIONS = [
  { value: 'INSTITUTION_STAFF', label: '기관직원' },
  { value: 'CIVIL_SERVANT', label: '공무원' },
  { value: 'CONTRACT', label: '계약직' },
];

const createDefaultDetail = (tenantCode) => ({
  tenantCode: tenantCode || '',
  empNo: '',
  personNm: '',
  birthDate: '',
  genderCode: 'M',
  mobileNo: '',
  email: '',
  zipCode: '',
  address: '',
  addressDtl: '',
  employmentClass: 'INSTITUTION_STAFF',
  legalRegime: 'LOCAL',
  firstAppointDate: getToday(),
  empStatus: 'ACTIVE',
  terminatedDate: '',
  terminatedReason: '',
  remark: '',
  photoPath: '',
  _pendingPhotoFile: null,
  lastEducationSummary: '',
  gradeNm: '',
  orgUnitNm: '',
  positionTitle: '',
  payStep: '',
  appointmentTypeNm: '',
});

const toDetailForm = (row, tenantCode) => {
  if (!row) return createDefaultDetail(tenantCode);
  return {
    tenantCode: row.tenantCode ?? tenantCode ?? '',
    empNo: row.empNo ?? '',
    personNm: row.personNm ?? '',
    birthDate: row.birthDate ?? '',
    genderCode: row.genderCode ?? 'U',
    mobileNo: row.mobileNo ?? '',
    email: row.email ?? '',
    zipCode: row.zipCode ?? '',
    address: row.address ?? '',
    addressDtl: row.addressDtl ?? '',
    employmentClass: row.employmentClass ?? 'INSTITUTION_STAFF',
    legalRegime: row.legalRegime ?? 'LOCAL',
    firstAppointDate: row.firstAppointDate ?? '',
    empStatus: row.empStatus ?? 'ACTIVE',
    terminatedDate: row.terminatedDate ?? '',
    terminatedReason: row.terminatedReason ?? '',
    remark: row.remark ?? '',
    photoPath: row.photoPath ?? '',
    _pendingPhotoFile: null,
    lastEducationSummary: row.lastEducationSummary ?? '',
    gradeNm: row.gradeNm ?? '',
    orgUnitNm: row.orgUnitNm ?? '',
    positionTitle: row.positionTitle ?? '',
    payStep: row.payStep != null ? String(row.payStep) : '',
    appointmentTypeNm: row.appointmentTypeNm ?? '',
  };
};

/** 그리드 컨테이너 리사이즈 시 컬럼 레이아웃 재계산 */
const GridPane = ({ gridRef, children }) => {
  const paneRef = useRef(null);

  useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      gridRef.current?.refresh?.();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [gridRef]);

  return <ResizablePane ref={paneRef}>{children}</ResizablePane>;
};

const MasterGridHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 8px 4px;
  flex-shrink: 0;
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.white};
  min-width: 0;
`;

const MasterGridTitle = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  flex-shrink: 0;
`;

const MasterGridMeta = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/**
 * 인사마스터 — 사원·인적 목록 + 상세 입력(CUD)
 */
const EmployeeMasterListInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [detailMod, setDetailMod] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [listCount, setListCount] = useState(0);
  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);

  const searchDefaultValues = {
    tenantCode,
    empNo: '',
    personNm: '',
    orgUnitNm: '',
    empStatus: '',
    employmentClass: '',
    asOfDate: getToday(),
  };

  const searchMethods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaultValues } });
  const detailMethods = useForm({ defaultValues: createDefaultDetail(tenantCode) });
  const { getValues: getSearchValues, setValue: setSearchValue } = searchMethods;
  const { reset: resetDetail, getValues: getDetailValues } = detailMethods;

  useEffect(() => {
    setSearchValue('tenantCode', tenantCode);
  }, [tenantCode, setSearchValue]);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사번', field: 'empNo', size: 80, minSize: 80, maxSize: 80, align: 'center', required: true }),
      hiCol.roTextColumn({ header: '성명', field: 'personNm', size: 64, minSize: 64, maxSize: 64, align: 'center', required: true }),
      hiCol.roTextColumn({ header: '재직', field: 'empStatusNm', size: 48, visible: false }),
      hiCol.roTextColumn({ header: '부서', field: 'orgUnitNm', size: 64, minSize: 64, maxSize: 64 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    selectQuery: 'Employee.selectEmployeeList',
    autoSearch: true,
    confirmCheck: false,
    enableContextMenu: true,
    duplicateExcludeKeys: ['employeeId', 'personId', 'tenantId', 'empNo', 'photoPath'],
    getDefaultRow: () => createDefaultDetail(getSearchValues().tenantCode || tenantCode),
  });

  const applyDetail = useCallback(
    (row, mod = 'U') => {
      const tc = getSearchValues().tenantCode || tenantCode;
      setSelectedRow(row);
      setDetailMod(mod);
      resetDetail(toDetailForm(row, tc));
    },
    [resetDetail, getSearchValues, tenantCode]
  );

  useEffect(() => {
    grid.onSelectRow = (rowData) => {
      if (!rowData) return;
      applyDetail(rowData, rowData.mod === 'A' ? 'A' : 'U');
    };
    grid.onRowClicked = ({ data }) => {
      if (!data) return;
      applyDetail(data, data.mod === 'A' ? 'A' : 'U');
    };
    grid.onAfterDataLoaded = (rows) => {
      setListCount(Array.isArray(rows) ? rows.length : 0);
    };
  }, [grid, applyDetail]);

  const handleAddClick = useCallback(() => {
    const tc = getSearchValues().tenantCode || tenantCode;
    const defaults = createDefaultDetail(tc);
    resetDetail(defaults);
    setDetailMod('A');
    setSelectedRow(null);
    grid.handleAdd({ ...defaults, mod: 'A', tenantCode: tc });
  }, [grid, resetDetail, getSearchValues, tenantCode]);

  const handleAddAboveClick = useCallback(() => {
    const tc = getSearchValues().tenantCode || tenantCode;
    const defaults = createDefaultDetail(tc);
    resetDetail(defaults);
    setDetailMod('A');
    setSelectedRow(null);
    grid.handleAddAbove({ ...defaults, mod: 'A', tenantCode: tc });
  }, [grid, resetDetail, getSearchValues, tenantCode]);

  const handleDuplicateClick = useCallback(async () => {
    await grid.handleDuplicate();
    setTimeout(() => {
      const row = grid.getSelectedRow?.() ?? gridRef.current?.getSelectedRow?.();
      if (row) applyDetail(row, 'A');
    }, 0);
  }, [grid, applyDetail, gridRef]);

  const buildSaveRow = useCallback(() => {
    const form = getDetailValues();
    const tc = getSearchValues().tenantCode || form.tenantCode || tenantCode;
    const mod = detailMod || (selectedRow ? 'U' : 'A');
    return {
      mod,
      tenantCode: tc,
      tenantId: selectedRow?.tenantId,
      employeeId: selectedRow?.employeeId,
      personId: selectedRow?.personId,
      empNo: form.empNo?.trim(),
      personNm: form.personNm?.trim(),
      birthDate: form.birthDate || null,
      genderCode: form.genderCode || 'U',
      mobileNo: form.mobileNo || null,
      email: form.email || null,
      zipCode: form.zipCode || null,
      address: form.address || null,
      addressDtl: form.addressDtl || null,
      remark: form.remark || null,
      employmentClass: form.employmentClass,
      legalRegime: form.legalRegime || 'LOCAL',
      firstAppointDate: form.firstAppointDate || null,
      empStatus: form.empStatus || 'ACTIVE',
      terminatedDate: form.terminatedDate || null,
      terminatedReason: form.terminatedReason || null,
    };
  }, [getDetailValues, getSearchValues, tenantCode, detailMod, selectedRow]);

  const handleSaveClick = useCallback(async () => {
    const row = buildSaveRow();
    if (!row.empNo || !row.personNm) {
      showAlert('사번과 성명은 필수입니다.', 'warning');
      return;
    }
    const ok = await showConfirm('저장하시겠습니까?', 'success');
    if (!ok) return;

    setSaving(true);
    try {
      const response = await api.post('/hr/employees/save', { rows: [row] });
      const data = response.data || {};
      if (data.Msg === 'success' || data.success) {
        const pendingFile = getDetailValues()._pendingPhotoFile;
        const savedEmpNo = row.empNo;
        const savedTenant = row.tenantCode;
        if (pendingFile) {
          try {
            const fd = new FormData();
            fd.append('tenantCode', savedTenant);
            fd.append('empNo', savedEmpNo);
            fd.append('file', pendingFile);
            await api.postForm('/hr/employees/photo', fd);
          } catch (pe) {
            showAlert(pe.data?.Msg || pe.message || '기본정보는 저장되었으나 사진 업로드에 실패했습니다.', 'warning');
          }
        }
        showAlert('저장되었습니다.', 'success');
        setDetailMod(null);
        await grid.handleSearch();
        const list = gridRef.current?.getData?.() || [];
        const found = list.find((r) => r.empNo === savedEmpNo);
        if (found) {
          applyDetail(found, 'U');
        }
      } else {
        showAlert(data.Msg || '저장에 실패했습니다.', 'error');
      }
    } catch (e) {
      showAlert(e.data?.Msg || e.message || '저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  }, [buildSaveRow, grid, applyDetail, gridRef, getDetailValues]);

  const handleDeleteClick = useCallback(async () => {
    if (detailMod === 'A') {
      await grid.handleDelete();
      resetDetail(createDefaultDetail(getSearchValues().tenantCode || tenantCode));
      setDetailMod(null);
      setSelectedRow(null);
      return;
    }
    if (!selectedRow?.employeeId) {
      showAlert('삭제할 사원을 선택해주세요.', 'warning');
      return;
    }
    const ok = await showConfirm('퇴직 처리하시겠습니까? (물리 삭제 없음)', 'warning');
    if (!ok) return;

    setSaving(true);
    try {
      const tc = getSearchValues().tenantCode || tenantCode;
      const form = getDetailValues();
      const response = await api.post('/hr/employees/save', {
        rows: [
          {
            mod: 'D',
            tenantCode,
            employeeId: selectedRow.employeeId,
            personId: selectedRow.personId,
            terminatedDate: form.terminatedDate || null,
            terminatedReason: form.terminatedReason || null,
          },
        ],
      });
      const data = response.data || {};
      if (data.Msg === 'success' || data.success) {
        showAlert('퇴직 처리되었습니다.', 'success');
        setDetailMod(null);
        setSelectedRow(null);
        resetDetail(createDefaultDetail(tenantCode));
        await grid.handleSearch();
      } else {
        showAlert(data.Msg || '삭제에 실패했습니다.', 'error');
      }
    } catch (e) {
      showAlert(e.data?.Msg || e.message || '삭제에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  }, [detailMod, selectedRow, grid, resetDetail, getSearchValues, getDetailValues, tenantCode]);

  const getMasterData = useCallback(() => gridRef.current?.getData?.() ?? [], []);
  const getMasterColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: MASTER_GRID_ID,
    label: '사원 목록',
    getData: getMasterData,
    getDataColumns: getMasterColumns,
    joinKeys: MASTER_JOIN_KEYS,
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '인사마스터' });

  const actionButtons = [
    {
      id: 'search',
      label: '조회',
      icon: '🔍',
      onClick: () =>
        grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
      hotkey: 'F7',
    },
    {
      id: 'add',
      label: '추가',
      icon: '➕',
      onClick: handleAddClick,
      hotkey: 'F8',
    },
    {
      id: 'delete',
      label: '삭제',
      icon: '❌',
      onClick: handleDeleteClick,
      hotkey: 'F9',
      variant: 'danger',
    },
    {
      id: 'save',
      label: '저장',
      icon: '✅',
      onClick: handleSaveClick,
      hotkey: 'F10',
      variant: 'success',
    },
    excelButton,
  ];

  const extraButtons = [
    {
      id: 'photoBulk',
      label: '사진일괄',
      icon: '🖼️',
      onClick: () => setBulkPhotoOpen(true),
    },
  ];

  
  const searchFields = [
    [
      { name: 'asOfDate', label: '기준일', type: 'date', width: CommonCss.w100, labelWidth: SEARCH_LABEL_W, required: true },
      { name: 'empNo', label: '사번', type: 'text', width: CommonCss.w70, labelWidth: SEARCH_LABEL_W },
      { name: 'personNm', label: '성명', type: 'text', width: CommonCss.w70, labelWidth: SEARCH_LABEL_W },
      { name: 'orgUnitNm', label: '부서', type: 'text', width: CommonCss.w80, labelWidth: SEARCH_LABEL_W },
      {
        name: 'empStatus',
        label: '재직상태',
        type: 'select',
        width: CommonCss.w80,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...EMP_STATUS_OPTIONS],
      },
      {
        name: 'employmentClass',
        label: '고용신분',
        type: 'select',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...EMPLOYMENT_CLASS_OPTIONS],
      },
    ],
  ];

  const detailTitle = detailMod === 'A'
    ? '사원 상세 — 신규 등록'
    : selectedRow
      ? `사원 상세 — ${selectedRow.personNm || ''} (${selectedRow.empNo || ''})`
      : '사원 상세 — 목록에서 사원을 선택하거나 [추가]를 누르세요';

  const activeTenantCode = getSearchValues().tenantCode || tenantCode;
  const employeeContext = useMemo(
    () => ({
      employeeId: detailMod === 'A' ? null : selectedRow?.employeeId ?? null,
      personId: selectedRow?.personId ?? null,
      tenantCode,
      empNo: selectedRow?.empNo ?? '',
      personNm: selectedRow?.personNm ?? '',
    }),
    [selectedRow, detailMod, tenantCode]
  );

  return (
    <PageContainer>
      <PageHeader>
        <Breadcrumb>
          <span>홈</span>
          <span className="separator">›</span>
          <span>인사관리</span>
          <span className="separator">›</span>
          <span className="current">인사마스터</span>
        </Breadcrumb>
      </PageHeader>
      <FormProvider {...searchMethods}>
        <ActionButtonGroup
          buttons={actionButtons}
          extraButtons={extraButtons}
          loading={grid.loading || saving}
        />
        <CollapsibleSearchPanel
          storageKey="hr-employee-master"
          prefsUserId={prefsUserId}
          summary="사원 조회조건"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={() => grid.handleSearch()}
            defaultValues={searchDefaultValues}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <ResizableSplit
          direction="horizontal"
          persistKey="hr_employee_master_lr_v4"
          initialPrimarySizePercent={22}
          minPrimarySizePx={280}
          minSecondarySizePx={360}
        >
          <GridPane gridRef={gridRef}>
            <GridExportFocusScope gridId={MASTER_GRID_ID}>
              <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                <MasterGridHeader>
                  <MasterGridTitle>사원 목록</MasterGridTitle>
                  <MasterGridMeta>
                    {grid.loading ? '조회 중…' : `총 ${listCount.toLocaleString()}건`}
                    {selectedRow?.empNo ? ` · 선택: ${selectedRow.personNm} (${selectedRow.empNo})` : ''}
                  </MasterGridMeta>
                </MasterGridHeader>
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <HiGridComponent
                    ref={gridRef}
                    columns={columns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={48}
                    useVirtualRows
                    {...grid.contextMenuProps}
                    onContextAddAbove={handleAddAboveClick}
                    onContextAddBelow={handleAddClick}
                    onContextDelete={handleDeleteClick}
                    onContextDuplicate={handleDuplicateClick}
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    exportFileName="사원목록"
                    gridId={MASTER_GRID_ID}
                    prefsUserId={prefsUserId}
                  />
                </div>
              </Card>
            </GridExportFocusScope>
          </GridPane>
          <ResizablePane style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
            <Card
              style={{
                padding: '8px 10px 10px',
                height: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <CardTitle style={{ marginBottom: 6, fontSize: '0.9rem', flexShrink: 0 }}>{detailTitle}</CardTitle>
              <FormProvider {...detailMethods}>
                <EmployeeMasterDetailTabs employeeContext={employeeContext} tenantCode={tenantCode} />
              </FormProvider>
            </Card>
          </ResizablePane>
        </ResizableSplit>
      </div>
      <PhotoBulkUploadDialog
        open={bulkPhotoOpen}
        tenantCode={tenantCode}
        onClose={() => setBulkPhotoOpen(false)}
        onDone={() => {
          grid.handleSearch().catch(() => {});
        }}
      />
      {excelDialog}
    </PageContainer>
  );
};

const EmployeeMasterList = () => (
  <GridExportProvider>
    <EmployeeMasterListInner />
  </GridExportProvider>
);

export default EmployeeMasterList;
